<template>
  <div class="ih-root" ref="root" :style="rootStyle">
    <div class="ih-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Stands: a static, slightly blurred layer under the scene (the SVG
           redraws every frame while the puck moves). -->
      <div class="ih-stands" :style="{ backgroundImage: crowd ? 'url(' + crowd + ')' : 'none' }"></div>
      <!-- Broadcast view of the rink: one flat SVG where every point (ice,
           boards, goals, puck) goes through the same projection (p3). -->
      <svg class="ih-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern :id="uid + 'net'" patternUnits="userSpaceOnUse" width="1.6" height="1.6">
            <path d="M0,0 L1.6,1.6 M1.6,0 L0,1.6" stroke="rgba(255,255,255,0.75)" stroke-width="0.25" />
          </pattern>
          <linearGradient :id="uid + 'ice'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#dfe8ef" />
            <stop offset="1" stop-color="#f6f9fb" />
          </linearGradient>
          <radialGradient :id="uid + 'sheen'" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stop-color="#fff" stop-opacity="0.9" />
            <stop offset="1" stop-color="#fff" stop-opacity="0" />
          </radialGradient>
          <linearGradient :id="uid + 'roof'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#050608" stop-opacity="0.95" />
            <stop offset="1" stop-color="#050608" stop-opacity="0" />
          </linearGradient>
          <radialGradient :id="uid + 'lamp'" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stop-color="#ff5a4f" stop-opacity="1" />
            <stop offset="0.4" stop-color="#ff2a1f" stop-opacity="0.6" />
            <stop offset="1" stop-color="#ff2a1f" stop-opacity="0" />
          </radialGradient>
          <linearGradient v-if="territory" :id="uid + 'terr'" gradientUnits="userSpaceOnUse" :x1="territory.x1" y1="0" :x2="territory.x2" y2="0">
            <stop offset="0" :stop-color="territory.color" stop-opacity="0.05" />
            <stop offset="0.75" :stop-color="territory.color" stop-opacity="0.3" />
            <stop offset="1" :stop-color="territory.color" stop-opacity="0.42" />
          </linearGradient>
          <clipPath :id="uid + 'rink'">
            <polygon :points="rink.ice" />
          </clipPath>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" height="70" :fill="'url(#' + uid + 'roof)'" />

        <!-- Glass and boards behind the ice: the whole rim is drawn at its
             height, the ice then covers what is not a far wall. -->
        <polygon :points="rink.glass" fill="rgba(200,225,245,0.12)" stroke="rgba(255,255,255,0.4)" stroke-width="0.7" />
        <polygon :points="rink.board" fill="#0f3a2c" />
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#0f3a2c" top-fill="#0f3a2c" />
        <path :d="rink.rail" fill="none" stroke="#f1f5f9" stroke-width="1.3" />
        <path :d="rink.kick" fill="none" stroke="#f5c518" stroke-width="1" />

        <!-- Goal lamps, on top of the end glass behind each goal. -->
        <g v-for="l in lamps" :key="'lamp' + l.side">
          <rect :x="l.x - 2.2" :y="l.y - 1.6" width="4.4" height="3.2" rx="0.8" :fill="lampSide === l.side ? '#ff3b30' : '#5b1512'" />
          <circle v-if="lampSide === l.side" :cx="l.x" :cy="l.y" r="14" :fill="'url(#' + uid + 'lamp)'" class="ih-lamp-glow" />
        </g>

        <!-- Ice. -->
        <polygon :points="rink.ice" :fill="'url(#' + uid + 'ice)'" />
        <g :clip-path="'url(#' + uid + 'rink)'">
          <ellipse :cx="STAGE_W / 2" :cy="sheenY" rx="150" ry="26" :fill="'url(#' + uid + 'sheen)'" opacity="0.7" />
          <!-- Possession: the team's territory, from its own end up to the
               puck, fading in toward the front. -->
          <polygon v-if="territory" :points="territory.points" :fill="'url(#' + uid + 'terr)'" class="ih-territory" />
          <!-- Logo (dark: the ice is white) under the ice along the near boards, clear of the low near board. -->
          <image v-if="showSirplayLogo" :href="PITCH_LOGO_DARK" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />
          <!-- Markings. -->
          <polygon v-for="(b, i) in markings.bands" :key="'band' + i" :points="b.points" :fill="b.color" />
          <path v-for="(c, i) in markings.creases" :key="'cr' + i" :d="c" fill="rgba(96,165,250,0.55)" stroke="#dc2626" stroke-width="0.8" />
          <g fill="none" stroke-linejoin="round">
            <path v-for="(d, i) in markings.red" :key="'r' + i" :d="d" stroke="#dc2626" stroke-width="0.9" />
            <path v-for="(d, i) in markings.blue" :key="'b' + i" :d="d" stroke="#2563eb" stroke-width="1" />
          </g>
          <ellipse v-for="(d, i) in markings.dots" :key="'d' + i" :cx="d.x" :cy="d.y" :rx="d.rx" :ry="d.ry" :fill="d.color" />
          <!-- Flat effects on the ice (faceoff ring). -->
          <path v-for="f in iceFx" :key="'ifx' + f.key" :d="f.d" class="ih-fx-ring" fill="none" stroke="#38bdf8" stroke-width="1.4" />
        </g>

        <!-- Goals: net behind, puck when it's inside, then the frame. -->
        <g v-for="g in goals" :key="'gb' + g.side" :class="{ 'ih-net-shake': netShake === g.side }">
          <polygon :points="g.back" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.8)" stroke-width="0.4" />
          <polygon :points="g.sideFar" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <path :d="g.base" stroke="#e5e7eb" stroke-width="0.8" fill="none" />
        </g>
        <g v-if="puckInNet">
          <path :d="puckView.body" fill="#0b0b0b" />
          <ellipse :cx="puckView.x" :cy="puckView.y - puckView.th" :rx="puckView.rx" :ry="puckView.ry" fill="#2d2d2d" />
        </g>
        <g v-for="g in goals" :key="'gf' + g.side" :class="{ 'ih-net-shake': netShake === g.side }">
          <polygon :points="g.sideNear" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <polygon :points="g.top" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <path :d="g.frame" stroke="#e11d24" stroke-width="1.5" fill="none" stroke-linejoin="round" />
        </g>

        <!-- Puck: trail while it flies, shadow, then the disc itself. -->
        <polyline v-if="trail.length > 1" :points="trail.join(' ')" fill="none" stroke="rgba(15,23,42,0.28)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        <g v-if="!puckInNet && puckVisible">
          <ellipse :cx="puckView.sx + 0.6" :cy="puckView.sy + 0.5" :rx="puckView.rx * 1.3" :ry="puckView.ry * 1.3" fill="rgba(15,23,42,0.28)" />
          <path :d="puckView.body" fill="#0b0b0b" />
          <ellipse :cx="puckView.x" :cy="puckView.y - puckView.th" :rx="puckView.rx" :ry="puckView.ry" fill="#2d2d2d" stroke="#4b5563" stroke-width="0.3" />
        </g>

        <!-- Bursts: save at the goal mouth, puck hitting the boards, goal. -->
        <circle v-for="f in fx" :key="'fx' + f.key" :cx="f.x" :cy="f.y" :r="f.r" fill="none" :stroke="f.color" stroke-width="1.6" class="ih-fx-burst" />

        <!-- Near boards and glass, in front of the ice. -->
        <rect x="0" :y="NEAR_Y" :width="STAGE_W" :height="STAGE_H - NEAR_Y" fill="#16181d" />
        <polygon :points="rink.nearBoard" fill="#0f3a2c" />
        <path :d="rink.nearRail" fill="none" stroke="#f1f5f9" stroke-width="1.4" />
        <polygon :points="rink.nearGlass" fill="rgba(200,225,245,0.05)" />
        <path :d="rink.nearGlassTop" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="0.8" />
      </svg>

      <!-- Clock. -->
      <div class="ih-clock">
        <b v-if="periodText">{{ periodText }}</b>
        <span>{{ time }}</span>
      </div>

      <!-- Penalty clocks still running (power play). -->
      <div v-if="penalties.length && !finished" class="ih-pp">
        <span v-for="p in penalties" :key="'pp' + p.id" class="ih-pp-chip" :style="{ borderColor: p.side === '1' ? homeCol : awayCol }">
          <svg viewBox="0 0 24 24" class="ih-pp-icon"><circle cx="12" cy="7" r="4" fill="currentColor" /><path d="M4,21 a8,7 0 0 1 16,0 Z" fill="currentColor" /></svg>
          {{ clockText(p.left) }}
        </span>
      </div>

      <!-- Card (penalty, penalty shot, video review): dims the rink for a
           few seconds. -->
      <transition name="ih-card">
        <div v-if="cardShown" :key="cardShown.key" class="ih-card-veil">
          <div class="ih-card" :style="{ '--side-color': cardShown.color }">
            <svg v-if="cardShown.kind === 'video'" class="ih-card-jersey" viewBox="0 0 40 40">
              <rect x="3" y="7" width="34" height="22" rx="3" fill="none" stroke="#38bdf8" stroke-width="2.5" />
              <line x1="14" y1="35" x2="26" y2="35" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round" />
              <line x1="20" y1="29" x2="20" y2="35" stroke="#38bdf8" stroke-width="2.5" />
              <path d="M16,13 L26,18 L16,23 Z" fill="#fff" />
            </svg>
            <svg v-else class="ih-card-jersey" viewBox="0 0 40 40">
              <path d="M14,4 L7,7 L1,14 L6,20 L9,18 L9,36 L31,36 L31,18 L34,20 L39,14 L33,7 L26,4 Q20,9 14,4 Z" :fill="cardShown.color" stroke="rgba(0,0,0,0.35)" stroke-width="0.8" stroke-linejoin="round" />
              <path d="M9,31 H31 V33.5 H9 Z M2.6,15.8 L6.8,20.6 L8.2,19.4 L4,14.6 Z M37.4,15.8 L33.2,20.6 L31.8,19.4 L36,14.6 Z" fill="#111" opacity="0.8" />
              <path d="M14,4 Q20,9 26,4" fill="none" stroke="#111" stroke-width="1.4" opacity="0.8" />
            </svg>
            <div class="ih-card-title">{{ cardShown.title }}</div>
            <div v-if="cardShown.sub" class="ih-card-min">{{ cardShown.sub }}</div>
            <div v-if="cardShown.team" class="ih-card-team">{{ cardShown.team }}</div>
          </div>
        </div>
      </transition>

      <!-- Goal, two acts on one full-stage panel: 1) "GOAL" with the scoring
           team over its colour; 2) split screen in the two teams' colours,
           the scorer's number rolling to the new score. -->
      <transition name="ih-goal-panel">
        <div v-if="goalShown" :key="goalShown.key" class="ih-goal-panel" :style="{ '--goal-color': goalShown.scoreSide === '2' ? awayCol : homeCol }">
          <transition name="ih-goal-act" mode="out-in">
            <div v-if="goalAct === 'word'" key="word" class="ih-goal-act ih-goal-act--word">
              <div class="ih-goal-stripes"></div>
              <div class="ih-goal-words">
                <div class="ih-goal-word">{{ goalShown.title }}</div>
                <div class="ih-goal-team">{{ goalShown.team }}<template v-if="goalSubtitle"> · {{ goalSubtitle }}</template></div>
              </div>
            </div>
            <div v-else key="split" class="ih-goal-act ih-goal-act--split">
              <div class="ih-goal-half ih-goal-half--home" :style="{ background: halfBg(homeCol) }">
                <span class="ih-goal-digit">
                  <transition name="ih-roll" mode="out-in"><span :key="'h' + rollHome" class="ih-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(homeCol) }">{{ rollHome }}</span></transition>
                </span>
              </div>
              <div class="ih-goal-half ih-goal-half--away" :style="{ background: halfBg(awayCol) }">
                <span class="ih-goal-digit">
                  <transition name="ih-roll" mode="out-in"><span :key="'a' + rollAway" class="ih-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(awayCol) }">{{ rollAway }}</span></transition>
                </span>
              </div>
            </div>
          </transition>
        </div>
      </transition>

      <!-- Match over: badge that stays up. -->
      <transition name="ih-final">
        <div v-if="finished" class="ih-final">
          <div class="ih-final-badge">
            <svg class="ih-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="ih-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div class="ih-final-score">
              <span :class="{ 'is-win': +scoreH > +scoreA }">{{ homeName }} {{ scoreH }}</span>
              <span class="ih-final-sep">-</span>
              <span :class="{ 'is-win': +scoreA > +scoreH }">{{ scoreA }} {{ awayName }}</span>
            </div>
            <div v-if="periods.length" class="ih-final-periods">
              <span v-for="(p, i) in periods" :key="'p' + i">{{ p.h }}-{{ p.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO_DARK, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width (see stageScaleMixin).
const STAGE_W = 460;
const STAGE_H = 236;
const NEAR_Y = 212;
// Rink projection (see makeProjection): u spans the rink length.
const { proj } = makeProjection({ farY: 98, nearY: NEAR_Y, farW: 336, nearW: 496, farCx: 230, nearCx: 230 });
// Rink in metres (IIHF): 60 x 30, corner radius 8.5. X along the length
// (0 = left end boards), Y across (0 = far boards), H height above the ice.
const CL = 60;
const CW = 30;
const R = 8.5;
const MID_Y = CW / 2;
const GOAL_LINE = 4;
const BLUE_LINE = 22.5;
const FACEOFF_X = 10;
const FACEOFF_DY = 7;
const NEUTRAL_X = 24;
const CIRCLE_R = 4.5;
const CREASE_R = 1.8;
// Goals, boards and glass are drawn bigger than life so they read at this size.
const GOAL_W = 2.6;
const GOAL_H = 1.7;
const GOAL_D = 1.4;
const BOARD_H = 2;
const GLASS_H = 2.4;
// Lower in front, so the puck along the near boards stays visible.
const NEAR_BOARD_H = 1.1;
const PUCK_R = 0.5;
// Timings (ms).
const GOAL_PANEL_DELAY = 800;
const GOAL_SPLIT_AT = 3400;
const GOAL_ROLL_AT = 5100;
const GOAL_END_AT = 9000;
const CARD_MS = 5200;
const FX_MS = 900;
// Pseudo incident sent back to MatchTracker when a power play runs out.
export const POWERPLAY_END_TYPE = 9101;

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const pick = list => list[Math.floor(Math.random() * list.length)];

// 3D point on the rink (metres) -> screen; m = px per metre at that depth.
function p3(X, Y, H = 0) {
  const g = proj(X / CL, Y / CW);
  const m = g.w / CL;
  return { x: g.x, y: g.y - H * m, m };
}
const poly = list => list.map(pt).join(' ');
const pathOf = list => 'M' + list.map(pt).join(' L');
// Home attacks the right goal, away the left one.
const goalX = end => (end === 'R' ? CL - GOAL_LINE : GOAL_LINE);
const attackEnd = team => (team === '1' ? 'R' : 'L');
const attackDir = team => (team === '1' ? 1 : -1);
const FACEOFF_DOTS = [
  { X: CL / 2, Y: MID_Y },
  ...[FACEOFF_X, CL - FACEOFF_X, NEUTRAL_X, CL - NEUTRAL_X].reduce((all, X) => all.concat([{ X, Y: MID_Y - FACEOFF_DY }, { X, Y: MID_Y + FACEOFF_DY }]), [])
];
const CENTRE = { X: CL / 2, Y: MID_Y, H: 0 };

// Rink outline (rounded rectangle), clockwise from the far-left straight.
function outline(n = 10) {
  const out = [];
  const arc = (cx, cy, a0) => {
    for (let i = 0; i <= n; i++) {
      const a = a0 + (Math.PI / 2) * i / n;
      out.push({ X: cx + Math.cos(a) * R, Y: cy + Math.sin(a) * R });
    }
  };
  arc(CL - R, R, -Math.PI / 2);
  arc(CL - R, CW - R, 0);
  arc(R, CW - R, Math.PI / 2);
  arc(R, R, Math.PI);
  return out;
}
const OUTLINE = outline();
// The near side of the rim: its boards stand in front of the ice.
const NEAR_PART = (() => {
  const i0 = OUTLINE.findIndex(p => p.Y >= CW - R - 0.01 && p.X > CL / 2);
  const i1 = OUTLINE.length - 1 - [...OUTLINE].reverse().findIndex(p => p.Y >= CW - R - 0.01 && p.X < CL / 2);
  return OUTLINE.slice(i0, i1 + 1);
})();

let fxSeq = 0;

export default {
  name: 'IceHockeyArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    scoreH: { type: [String, Number], default: 0 },
    scoreA: { type: [String, Number], default: 0 },
    // '1' home / '2' away / null.
    possessionSide: { type: String, default: null },
    time: { type: String, default: '' },
    // Match clock in seconds: penalty clocks run down with it.
    clockSeconds: { type: Number, default: 0 },
    periodText: { type: String, default: '' },
    // Incident on screen (MatchTracker's displayAction): type, side, key.
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    actionKey: { type: String, default: '' },
    // { key, side, minutes, team }
    penalty: { type: Object, default: null },
    // { key, title, subtitle, team, scoreSide, penaltyShot, fromHome, fromAway, homeScore, awayScore }
    goalCelebration: { type: Object, default: null },
    // Goals per period so far, [{ h, a }] — shown on the final badge.
    periods: { type: Array, default: () => [] },
    finished: { type: Boolean, default: false },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    return {
      PITCH_LOGO_DARK,
      STAGE_W,
      STAGE_H,
      NEAR_Y,
      ZOOM: 1,
      crowd: '',
      puck: { ...CENTRE },
      puckVisible: true,
      // Scripted play (shot, faceoff, ...) or null while the puck roams.
      seq: null,
      wander: null,
      // Where the possession tint ends: follows the puck with some lag.
      front: CL / 2,
      trail: [],
      raf: null,
      fx: [],
      iceFx: [],
      fxTimers: [],
      lampSide: null,
      netShake: null,
      cardShown: null,
      cardTimer: null,
      penalties: [],
      // Team of the last incident: keeps the puck in its zone when the feed
      // has no possession reading.
      lastTeam: null,
      goalShown: null,
      goalAct: 'word',
      goalRolled: false,
      goalTimers: [],
      rollHome: 0,
      rollAway: 0
    };
  },
  watch: {
    actionKey() {
      if (this.goalShown || this.finished) return;
      const t = this.actionType;
      const s = this.actionSide;
      const team = s === '1' || s === '2' ? s : null;
      const other = team === '1' ? '2' : '1';
      if (t === 1074) {
        this.showCard({ key: this.actionKey, kind: 'video', title: this.$t('hockey.videoreview'), side: team, team: team ? this.teamName(team) : '' });
        return;
      }
      if (!team) return;
      // Save: the side is the goalkeeper's team, the shot comes from the other.
      const shooter = t === 96 ? other : team;
      if (t !== 33) this.lastTeam = shooter;
      if (t === 2 || t === 96) this.playShot(shooter, 'save');
      else if (t === 3) this.playShot(team, 'wide');
      else if (t === 16) this.playShot(team, 'blocked');
      else if (t === 40) this.playShot(team, 'save', true);
      else if (t === 8) {
        this.showCard({ key: this.actionKey, kind: 'penaltyshot', title: this.$t('hockey.penaltyshot'), side: team, team: this.teamName(team) });
        this.playPenaltyShot(team);
      }
      else if (t === 15) this.playOffside(team);
      else if (t === POWERPLAY_END_TYPE) this.showCard({ key: this.actionKey, kind: 'ppend', title: this.$t('hockey.powerplayend'), side: team, team: this.teamName(team) });
      else if (t === 33) this.burst(this.puck.X, this.puck.Y, 0.6, '#cbd5e1', 5);
      else if (t === 2438) this.playPressure(team);
      else if (t === 2048) this.playFaceoff(team);
      else if (t === 2393) this.playIcing(team);
    },
    'penalty.key'() {
      const p = this.penalty;
      if (!p || this.finished) return;
      this.showCard({ key: p.key, kind: 'penalty', title: this.$t('hockey.penalty'), sub: `${p.minutes} ${this.$t('hockey.min')}`, side: p.side, team: p.team });
      const total = (p.minutes || 2) * 60;
      this.penalties = [...this.penalties, { id: p.key, side: p.side, minor: p.minutes === 2, left: total }].slice(-4);
      // Play restarts with a faceoff in the penalised team's zone.
      if (!this.goalShown) {
        const dotX = p.side === '1' ? FACEOFF_X : CL - FACEOFF_X;
        this.playToDot({ X: dotX, Y: MID_Y + pick([-1, 1]) * FACEOFF_DY });
      }
    },
    clockSeconds(n, o) {
      const d = Math.abs(n - o);
      // A jump (new period, clock resync) is not time played.
      if (!d || d > 90 || !this.penalties.length) return;
      const next = this.penalties.map(p => ({ ...p, left: p.left - d }));
      this.penalties = next.filter(p => p.left > 0);
      // A team with no penalty left running is back to full strength: the
      // power play is over (MatchTracker announces it like an incident).
      const over = [...new Set(next.filter(p => p.left <= 0).map(p => p.side))];
      over.filter(side => !this.penalties.some(p => p.side === side)).forEach(side => this.$emit('powerplay-end', side));
    },
    'goalCelebration.key'(key) {
      const g = this.goalCelebration;
      // A goal is only ever replaced by the next goal, never cut short.
      if (!key || !g || this.finished) return;
      this.startGoal(g);
    },
    'goalCelebration.homeScore'(v) { if (this.goalShown && this.goalRolled) this.rollHome = v; },
    'goalCelebration.awayScore'(v) { if (this.goalShown && this.goalRolled) this.rollAway = v; },
    finished(val) {
      if (!val) return;
      this.seq = null;
      this.puck = { ...CENTRE };
      this.penalties = [];
      this.cardShown = null;
    }
  },
  mounted() {
    this.crowd = crowdTexture('arena');
    this.raf = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.cardTimer);
    this.goalTimers.forEach(clearTimeout);
    this.fxTimers.forEach(clearTimeout);
  },
  methods: {
    tint(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return `rgba(45,95,174,${alpha})`;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    },
    isLight(hex) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return false;
      const n = parseInt(m[1], 16);
      return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) > 185;
    },
    // A white/pale shirt would vanish on the ice: tint with slate instead.
    iceTint(hex) { return this.isLight(hex) ? '#475569' : hex; },
    textOn(hex) { return this.isLight(hex) ? '#1f2937' : '#fff'; },
    // Team colour, lighter toward the centre line and darker toward the edge.
    halfBg(hex) {
      return `linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(0,0,0,0.38) 100%), ${hex}`;
    },
    teamName(team) { return team === '1' ? this.homeName : this.awayName; },
    showCard(card) {
      clearTimeout(this.cardTimer);
      this.cardShown = { ...card, color: card.side === '1' ? this.homeCol : (card.side === '2' ? this.awayCol : '#38bdf8') };
      this.cardTimer = setTimeout(() => { this.cardShown = null; }, CARD_MS);
    },
    clockText(sec) {
      const s = Math.max(0, Math.ceil(sec));
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    },
    later(fn, ms, list = this.fxTimers) {
      const id = setTimeout(() => {
        const i = list.indexOf(id);
        if (i >= 0) list.splice(i, 1);
        fn();
      }, ms);
      list.push(id);
    },
    burst(X, Y, H, color, r = 7) {
      const p = p3(X, Y, H);
      const key = ++fxSeq;
      this.fx = [...this.fx, { key, x: p.x, y: p.y, r, color }];
      this.later(() => { this.fx = this.fx.filter(f => f.key !== key); }, FX_MS);
    },
    ring(X, Y) {
      const key = ++fxSeq;
      const pts = [];
      for (let i = 0; i <= 32; i++) {
        const a = Math.PI * 2 * i / 32;
        pts.push(p3(X + Math.cos(a) * 2.4, Y + Math.sin(a) * 2.4));
      }
      this.iceFx = [...this.iceFx, { key, d: pathOf(pts) }];
      this.later(() => { this.iceFx = this.iceFx.filter(f => f.key !== key); }, FX_MS);
    },
    onEvent(ev) {
      if (!ev) return;
      const { kind, X, Y, H = 0, side } = ev;
      if (kind === 'save') this.burst(X, Y, H, '#ffffff', 8);
      else if (kind === 'boards') this.burst(X, Y, 0.8, '#e2e8f0', 6);
      else if (kind === 'block') this.burst(X, Y, 0.4, '#fbbf24', 6);
      else if (kind === 'faceoff') this.ring(X, Y);
      else if (kind === 'goal') {
        this.burst(X, Y, H, '#ff3b30', 11);
        this.lampSide = side;
        this.netShake = side;
        this.later(() => { this.netShake = null; }, 1400);
        this.later(() => { this.lampSide = null; }, 4500);
      }
    },
    // One animation loop for everything that moves.
    tick(ts) {
      if (this.seq) this.stepSeq(ts);
      else if (!this.finished) this.stepWander(ts);
      else if (this.trail.length) this.trail = [];
      this.front += (this.puck.X - this.front) * 0.04;
      this.raf = requestAnimationFrame(this.tick);
    },
    // Idle: the team in possession moves the puck around the attacking
    // zone — carries on a curve, and quick passes.
    stepWander(ts) {
      let w = this.wander;
      if (!w || w.team !== this.playTeam || ts - w.t0 >= w.dur) {
        w = this.nextWander(ts);
        this.wander = w;
      }
      const t = clamp((ts - w.t0) / w.dur, 0, 1);
      const e = w.pass ? 1 - (1 - t) * (1 - t) : t * t * (3 - 2 * t);
      const X = (1 - e) * (1 - e) * w.from.X + 2 * (1 - e) * e * w.ctrl.X + e * e * w.to.X;
      const Y = (1 - e) * (1 - e) * w.from.Y + 2 * (1 - e) * e * w.ctrl.Y + e * e * w.to.Y;
      this.puck = { X, Y, H: 0 };
      this.pushTrail(w.pass && t < 0.7);
    },
    nextWander(ts) {
      const team = this.playTeam;
      const from = { X: this.puck.X, Y: this.puck.Y };
      let to;
      if (team) {
        const gx = goalX(attackEnd(team));
        const dir = attackDir(team);
        to = { X: gx - dir * rand(1, 17), Y: rand(3, CW - 4) };
        // Keep out of the crease.
        if (Math.abs(to.X - gx) < 3.5 && Math.abs(to.Y - MID_Y) < 3.5) to.Y = MID_Y + pick([-1, 1]) * rand(4, 7);
      } else {
        to = { X: CL / 2 + rand(-6, 6), Y: MID_Y + rand(-6, 6) };
      }
      const dist = Math.hypot(to.X - from.X, to.Y - from.Y);
      // A new team in possession breaks out with a long pass first.
      const pass = (this.wander && this.wander.team !== team) || Math.random() < 0.4;
      const bend = pass ? 0 : rand(-0.35, 0.35) * dist;
      const nx = dist ? -(to.Y - from.Y) / dist : 0;
      const ny = dist ? (to.X - from.X) / dist : 0;
      const ctrl = { X: (from.X + to.X) / 2 + nx * bend, Y: clamp((from.Y + to.Y) / 2 + ny * bend, 1, CW - 1) };
      const dur = pass ? clamp(dist * 45, 380, 900) : clamp(dist * 140, 900, 2200);
      return { team, from, to, ctrl, pass, t0: ts, dur };
    },
    pushTrail(active) {
      if (active) {
        const p = this.puckView;
        this.trail = [...this.trail.slice(-7), pt({ x: p.x, y: p.y - p.th / 2 })];
      } else if (this.trail.length) {
        this.trail = this.trail.slice(1);
      }
    },
    // Scripted play: a list of { to, dur, ease, peak, fast, ev } segments
    // starting from wherever the puck is now.
    runSeq(segs) {
      let from = { ...this.puck };
      const list = segs.map(s => {
        const to = { H: 0, ...(s.to || from) };
        const out = { ...s, from, to };
        from = to;
        return out;
      });
      this.seq = { segs: list, i: 0, t0: null };
    },
    stepSeq(ts) {
      const sq = this.seq;
      if (sq.t0 === null) sq.t0 = ts;
      let sg = sq.segs[sq.i];
      let t = (ts - sq.t0) / sg.dur;
      while (t >= 1) {
        this.onEvent(sg.ev);
        sq.i++;
        if (sq.i >= sq.segs.length) {
          this.puck = { ...sg.to };
          this.seq = null;
          this.wander = null;
          return;
        }
        sq.t0 += sg.dur;
        sg = sq.segs[sq.i];
        t = (ts - sq.t0) / sg.dur;
      }
      let e = t;
      if (sg.ease === 'smooth') e = t * t * (3 - 2 * t);
      else if (sg.ease === 'out') e = 1 - (1 - t) * (1 - t);
      const X = sg.from.X + (sg.to.X - sg.from.X) * e;
      const Y = sg.from.Y + (sg.to.Y - sg.from.Y) * e;
      const H = sg.from.H + (sg.to.H - sg.from.H) * e + 4 * (sg.peak || 0) * t * (1 - t);
      this.puck = { X, Y, H: Math.max(0, H) };
      this.pushTrail(!!sg.fast);
    },
    // Shot by `team` at the goal it attacks: 'goal' | 'save' | 'wide' | 'blocked'.
    // `alone`: penalty shot, taken from straight in front of the goal.
    playShot(team, outcome, alone = false) {
      const end = attackEnd(team);
      const gx = goalX(end);
      const dir = attackDir(team);
      const spot = alone ? { X: gx - dir * rand(3.5, 5), Y: MID_Y + rand(-1.2, 1.2) } : { X: gx - dir * rand(5, 12), Y: MID_Y + rand(-6.5, 6.5) };
      const segs = [{ to: spot, dur: 520, ease: 'smooth' }];
      if (outcome === 'goal') {
        const inNet = { X: gx + dir * (GOAL_D * 0.6), Y: MID_Y + rand(-0.7, 0.7), H: rand(0.2, 1) };
        segs.push({ to: inNet, dur: 240, fast: true, peak: 0.25, ev: { kind: 'goal', X: gx, Y: MID_Y, H: GOAL_H / 2, side: end } });
        segs.push({ to: { X: inNet.X + dir * 0.3, Y: inNet.Y, H: 0 }, dur: 260, ease: 'out' });
        segs.push({ dur: 2600 });
      } else if (outcome === 'save') {
        const mouth = { X: gx - dir * 0.7, Y: MID_Y + rand(-0.9, 0.9), H: rand(0.2, 0.9) };
        segs.push({ to: mouth, dur: 230, fast: true, ev: { kind: 'save', ...mouth } });
        segs.push({ to: { X: gx - dir * rand(1, 4), Y: pick([rand(2.5, 6), rand(CW - 6, CW - 2.5)]), H: 0 }, dur: 620, ease: 'out', fast: true });
        segs.push({ dur: 900 });
      } else if (outcome === 'wide') {
        const boards = { X: end === 'R' ? CL - 0.6 : 0.6, Y: MID_Y + pick([-1, 1]) * rand(2.5, 6.5), H: 0.3 };
        segs.push({ to: boards, dur: 380, fast: true, ev: { kind: 'boards', ...boards } });
        segs.push({ to: { X: gx + dir * 1.5, Y: boards.Y < MID_Y ? rand(2, 4) : rand(CW - 4, CW - 2), H: 0 }, dur: 700, ease: 'out', fast: true });
        segs.push({ dur: 700 });
      } else {
        const block = { X: spot.X + dir * rand(1.8, 3.2), Y: spot.Y + rand(-0.8, 0.8), H: 0.2 };
        segs.push({ to: block, dur: 150, fast: true, ev: { kind: 'block', ...block } });
        segs.push({ to: { X: block.X - dir * rand(1, 3), Y: clamp(block.Y + pick([-1, 1]) * rand(3, 5), 2, CW - 2), H: 0 }, dur: 520, ease: 'out' });
        segs.push({ dur: 700 });
      }
      this.runSeq(segs);
    },
    // Puck back on a faceoff dot, dropped there.
    playToDot(dot) {
      this.runSeq([
        { to: { X: dot.X, Y: dot.Y }, dur: 900, ease: 'smooth' },
        { dur: 1100, ev: { kind: 'faceoff', X: dot.X, Y: dot.Y } },
        { dur: 800 }
      ]);
    },
    // Faceoff won by `team`: on the nearest dot, then pulled back to its side.
    playFaceoff(team) {
      const dot = FACEOFF_DOTS.reduce((a, b) => (Math.hypot(b.X - this.puck.X, b.Y - this.puck.Y) < Math.hypot(a.X - this.puck.X, a.Y - this.puck.Y) ? b : a));
      const dir = attackDir(team);
      this.runSeq([
        { to: { X: dot.X, Y: dot.Y }, dur: 600, ease: 'smooth' },
        { dur: 500, ev: { kind: 'faceoff', X: dot.X, Y: dot.Y } },
        { to: { X: clamp(dot.X - dir * rand(3, 5), 2, CL - 2), Y: clamp(dot.Y + rand(-3, 3), 2, CW - 2) }, dur: 420, ease: 'out', fast: true },
        { dur: 500 }
      ]);
    },
    // Icing: dumped from its own half to the far end, faceoff in its zone.
    playIcing(team) {
      const dir = attackDir(team);
      const far = { X: dir === 1 ? CL - 0.8 : 0.8, Y: rand(4, CW - 4), H: 0.1 };
      const dotX = team === '1' ? FACEOFF_X : CL - FACEOFF_X;
      this.runSeq([
        { to: { X: CL / 2 - dir * rand(5, 10), Y: rand(6, CW - 6) }, dur: 500, ease: 'smooth' },
        { to: far, dur: 1000, fast: true, ev: { kind: 'boards', ...far } },
        { dur: 600 },
        { to: { X: dotX, Y: MID_Y + pick([-1, 1]) * FACEOFF_DY }, dur: 1100, ease: 'smooth' },
        { dur: 900, ev: { kind: 'faceoff', X: dotX, Y: MID_Y } }
      ]);
    },
    // Penalty shot awarded: puck on the centre dot, the shooter skates in
    // alone (the shot itself comes as a goal or a missed penalty).
    playPenaltyShot(team) {
      const gx = goalX(attackEnd(team));
      const dir = attackDir(team);
      this.runSeq([
        { to: { X: CENTRE.X, Y: CENTRE.Y }, dur: 800, ease: 'smooth' },
        { dur: 900, ev: { kind: 'faceoff', X: CENTRE.X, Y: CENTRE.Y } },
        { to: { X: gx - dir * 9, Y: MID_Y + rand(-2, 2) }, dur: 2400, ease: 'smooth' },
        { dur: 1500 }
      ]);
    },
    // Offside: the puck crosses the blue line, play stops, faceoff on the
    // neutral-zone dot outside it.
    playOffside(team) {
      const dir = attackDir(team);
      const blue = team === '1' ? CL - BLUE_LINE : BLUE_LINE;
      const y = rand(5, CW - 5);
      const dot = { X: team === '1' ? CL - NEUTRAL_X : NEUTRAL_X, Y: MID_Y + (y < MID_Y ? -1 : 1) * FACEOFF_DY };
      this.runSeq([
        { to: { X: blue - dir * 3, Y: y }, dur: 700, ease: 'smooth' },
        { to: { X: blue + dir * 2, Y: y + rand(-1.5, 1.5) }, dur: 500, fast: true, ev: { kind: 'block', X: blue, Y: y } },
        { dur: 700 },
        { to: dot, dur: 1000, ease: 'smooth' },
        { dur: 900, ev: { kind: 'faceoff', ...dot } }
      ]);
    },
    // Dangerous attack: the puck is worked into the slot.
    playPressure(team) {
      const gx = goalX(attackEnd(team));
      const dir = attackDir(team);
      this.runSeq([
        { to: { X: gx - dir * rand(8, 12), Y: pick([rand(4, 8), rand(CW - 8, CW - 4)]) }, dur: 700, ease: 'smooth' },
        { to: { X: gx - dir * rand(4, 7), Y: MID_Y + rand(-3, 3) }, dur: 450, fast: true, ease: 'out' },
        { dur: 800 }
      ]);
    },
    startGoal(g) {
      this.goalTimers.forEach(clearTimeout);
      this.goalTimers = [];
      clearTimeout(this.cardTimer);
      this.cardShown = null;
      const scorer = g.scoreSide === '2' ? '2' : '1';
      // Power-play goal: the oldest minor of the other team is over.
      const pk = this.penalties.findIndex(p => p.side !== scorer && p.minor);
      if (pk >= 0) this.penalties = this.penalties.filter((p, i) => i !== pk);
      this.playShot(scorer, 'goal', !!g.penaltyShot);
      this.later(() => {
        this.goalShown = g;
        this.goalAct = 'word';
        this.goalRolled = false;
        this.rollHome = g.fromHome;
        this.rollAway = g.fromAway;
      }, GOAL_PANEL_DELAY, this.goalTimers);
      this.later(() => { this.goalAct = 'split'; }, GOAL_SPLIT_AT, this.goalTimers);
      this.later(() => {
        const cur = this.goalCelebration && this.goalCelebration.key === g.key ? this.goalCelebration : g;
        this.goalRolled = true;
        this.rollHome = cur.homeScore;
        this.rollAway = cur.awayScore;
      }, GOAL_ROLL_AT, this.goalTimers);
      this.later(() => {
        this.goalShown = null;
        // Restart from the centre faceoff (hidden by the panel until now).
        this.seq = null;
        this.puck = { ...CENTRE };
        this.front = CL / 2;
        this.wander = null;
        this.playToDot(CENTRE);
      }, GOAL_END_AT, this.goalTimers);
    }
  },
  computed: {
    logoDecal() {
      return floorLogo(p3, CL / 2, CW - 4.5, 1.9);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'ih' + this._uid + '-'; },
    homeCol() { return this.homeColor || '#2d5fae'; },
    // Team moving the puck: the feed's possession, else the last incident's.
    playTeam() {
      const p = this.possessionSide;
      if (p === '1' || p === '2') return p;
      return this.lastTeam;
    },
    // Kind of goal (power play, empty net, ...): can arrive after the goal.
    goalSubtitle() {
      const g = this.goalShown;
      if (!g) return '';
      const live = this.goalCelebration && this.goalCelebration.key === g.key ? this.goalCelebration : g;
      return live.subtitle || '';
    },
    awayCol() { return this.awayColor || '#c0392b'; },
    rootStyle() {
      return { height: (STAGE_H * this.scale) + 'px' };
    },
    sheenY() { return p3(CL / 2, CW * 0.35).y; },
    rink() {
      const at = H => OUTLINE.map(p => p3(p.X, p.Y, H));
      const near = H => NEAR_PART.map(p => p3(p.X, p.Y, H));
      return {
        ice: poly(at(0)),
        board: poly(at(BOARD_H)),
        glass: poly(at(BOARD_H + GLASS_H)),
        rail: pathOf(at(BOARD_H)) + ' Z',
        kick: pathOf(at(0.3)) + ' Z',
        nearBoard: poly([...near(0), ...near(NEAR_BOARD_H).reverse()]),
        nearRail: pathOf(near(NEAR_BOARD_H)),
        nearGlass: poly([...near(NEAR_BOARD_H), ...near(NEAR_BOARD_H + GLASS_H).reverse()]),
        nearGlassTop: pathOf(near(NEAR_BOARD_H + GLASS_H))
      };
    },
    boards() {
      const lo = 0.35;
      const hi = BOARD_H - 0.3;
      const board = (key, a, b, count, item) => {
        const pa = p3(a.X, a.Y, lo);
        const pb = p3(b.X, b.Y, lo);
        return makeBoard(key, pa, pb, pa.y - p3(a.X, a.Y, hi).y, pb.y - p3(b.X, b.Y, hi).y, count, item);
      };
      return [
        board('far', { X: R, Y: 0 }, { X: CL - R, Y: 0 }, 6, 30),
        board('left', { X: 0, Y: CW - R }, { X: 0, Y: R }, 1, 22),
        board('right', { X: CL, Y: R }, { X: CL, Y: CW - R }, 1, 22)
      ];
    },
    lamps() {
      return ['L', 'R'].map(side => {
        const p = p3(side === 'L' ? 0 : CL, MID_Y, BOARD_H + GLASS_H + 0.4);
        return { side, x: p.x, y: p.y };
      });
    },
    markings() {
      const band = (x0, x1, color) => ({ points: poly([p3(x0, -1), p3(x1, -1), p3(x1, CW + 1), p3(x0, CW + 1)]), color });
      const circle = (cx, cy, r, a0 = 0, a1 = Math.PI * 2, n = 40) => {
        const out = [];
        for (let i = 0; i <= n; i++) {
          const a = a0 + (a1 - a0) * i / n;
          out.push(p3(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
        }
        return out;
      };
      const dot = (X, Y, r, color) => {
        const c = p3(X, Y);
        const top = p3(X, Y - r);
        return { x: c.x, y: c.y, rx: r * c.m, ry: Math.max(0.6, c.y - top.y), color };
      };
      const red = [];
      const blue = [];
      const creases = [];
      const dots = [];
      const bands = [
        band(CL / 2 - 0.2, CL / 2 + 0.2, '#dc2626'),
        band(BLUE_LINE - 0.2, BLUE_LINE + 0.2, '#2563eb'),
        band(CL - BLUE_LINE - 0.2, CL - BLUE_LINE + 0.2, '#2563eb')
      ];
      // Centre circle and dot.
      blue.push(pathOf(circle(CL / 2, MID_Y, CIRCLE_R)));
      dots.push(dot(CL / 2, MID_Y, 0.35, '#2563eb'));
      [GOAL_LINE, CL - GOAL_LINE].forEach(gx => {
        const dir = gx < CL / 2 ? 1 : -1;
        red.push(seg(p3(gx, -1), p3(gx, CW + 1)));
        // Crease and the trapezoid behind the goal.
        creases.push(pathOf(circle(gx, MID_Y, CREASE_R, dir === 1 ? -Math.PI / 2 : Math.PI / 2, dir === 1 ? Math.PI / 2 : Math.PI * 1.5, 16)) + ' Z');
        const back = gx - dir * GOAL_LINE;
        red.push(seg(p3(gx, MID_Y - 3.4), p3(back, MID_Y - 4.6)));
        red.push(seg(p3(gx, MID_Y + 3.4), p3(back, MID_Y + 4.6)));
        // End-zone faceoff circles, with their hash marks.
        const fx = gx + dir * (FACEOFF_X - GOAL_LINE);
        [-FACEOFF_DY, FACEOFF_DY].forEach(dy => {
          const cy = MID_Y + dy;
          red.push(pathOf(circle(fx, cy, CIRCLE_R)));
          [-0.9, 0.9].forEach(hx => {
            red.push(seg(p3(fx + hx, cy - CIRCLE_R), p3(fx + hx, cy - CIRCLE_R - 0.7)));
            red.push(seg(p3(fx + hx, cy + CIRCLE_R), p3(fx + hx, cy + CIRCLE_R + 0.7)));
          });
          dots.push(dot(fx, cy, 0.45, '#dc2626'));
        });
      });
      [NEUTRAL_X, CL - NEUTRAL_X].forEach(X => {
        dots.push(dot(X, MID_Y - FACEOFF_DY, 0.4, '#dc2626'));
        dots.push(dot(X, MID_Y + FACEOFF_DY, 0.4, '#dc2626'));
      });
      return { bands, red, blue, creases, dots };
    },
    goals() {
      return ['L', 'R'].map(side => {
        const gx = goalX(side);
        const dir = side === 'L' ? 1 : -1;
        const y0 = MID_Y - GOAL_W / 2;
        const y1 = MID_Y + GOAL_W / 2;
        const xb = gx - dir * GOAL_D;
        const xt = gx - dir * GOAL_D * 0.35;
        return {
          side,
          back: poly([p3(xb, y0), p3(xb, y1), p3(xt, y1, GOAL_H), p3(xt, y0, GOAL_H)]),
          sideFar: poly([p3(gx, y0), p3(xb, y0), p3(xt, y0, GOAL_H), p3(gx, y0, GOAL_H)]),
          sideNear: poly([p3(gx, y1), p3(xb, y1), p3(xt, y1, GOAL_H), p3(gx, y1, GOAL_H)]),
          top: poly([p3(gx, y0, GOAL_H), p3(gx, y1, GOAL_H), p3(xt, y1, GOAL_H), p3(xt, y0, GOAL_H)]),
          base: pathOf([p3(gx, y0), p3(xb, y0), p3(xb, y1), p3(gx, y1)]),
          frame: pathOf([p3(gx, y0), p3(gx, y0, GOAL_H), p3(gx, y1, GOAL_H), p3(gx, y1)])
        };
      });
    },
    // Puck behind a goal line, inside the mouth: drawn between net and frame.
    puckInNet() {
      const b = this.puck;
      if (Math.abs(b.Y - MID_Y) > GOAL_W / 2) return false;
      return b.X < GOAL_LINE || b.X > CL - GOAL_LINE;
    },
    puckView() {
      const b = this.puck;
      const g = p3(b.X, b.Y, 0);
      const up = p3(b.X, b.Y, b.H);
      const rx = PUCK_R * g.m;
      const ry = rx * 0.42;
      const th = rx * 0.5;
      const x = up.x;
      const y = up.y;
      const body = `M${(x - rx).toFixed(2)},${(y - th).toFixed(2)} L${(x - rx).toFixed(2)},${y.toFixed(2)} A${rx.toFixed(2)},${ry.toFixed(2)} 0 0 0 ${(x + rx).toFixed(2)},${y.toFixed(2)} L${(x + rx).toFixed(2)},${(y - th).toFixed(2)} Z`;
      return { x, y, sx: g.x, sy: g.y, rx, ry, th, body };
    },
    territory() {
      const team = this.playTeam;
      if (!team || this.finished || this.goalShown) return null;
      const x0 = team === '1' ? -1 : CL + 1;
      const front = clamp(this.front + attackDir(team) * 1.5, 1, CL - 1);
      return {
        points: poly([p3(x0, -1), p3(front, -1), p3(front, CW + 1), p3(x0, CW + 1)]),
        x1: p3(x0, MID_Y).x.toFixed(1),
        x2: p3(front, MID_Y).x.toFixed(1),
        color: this.iceTint(team === '1' ? this.homeCol : this.awayCol)
      };
    }
  }
};
</script>

<style scoped>
.ih-root { position: relative; width: 100%; overflow: hidden; background: #101418; }
.ih-stage { position: absolute; top: 0; left: 0; width: 460px; height: 236px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.ih-stands { position: absolute; inset: -2px; background-color: #101418; background-size: 90px 45px; background-repeat: repeat; filter: blur(0.6px) brightness(0.7); }
.ih-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.ih-territory { transition: opacity 0.4s ease; animation: ih-territory 2.6s ease-in-out infinite; }
@keyframes ih-territory { 0%, 100% { opacity: 0.8; } 50% { opacity: 1; } }
.ih-lamp-glow { animation: ih-lamp 0.45s ease-in-out infinite alternate; }
@keyframes ih-lamp { from { opacity: 0.35; } to { opacity: 1; } }
.ih-net-shake { animation: ih-shake 0.12s linear 10; }
@keyframes ih-shake { 0%, 100% { transform: translate(0, 0); } 25% { transform: translate(0.5px, -0.3px); } 75% { transform: translate(-0.5px, 0.3px); } }
.ih-fx-burst { transform-box: fill-box; transform-origin: 50% 50%; animation: ih-burst 0.9s ease-out forwards; }
@keyframes ih-burst { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }
.ih-fx-ring { transform-box: fill-box; transform-origin: 50% 50%; animation: ih-ring 0.9s ease-out forwards; }
@keyframes ih-ring { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }

/* Clock. */
.ih-clock {
  position: absolute; top: 6px; left: 50%; transform: translateX(-50%); z-index: 2;
  display: flex; align-items: center; gap: 6px; padding: 2px 9px;
  background: rgba(0, 0, 0, 0.62); border-radius: 3px;
  font: 700 var(--font-size-3xl)/1.25 'Roboto Mono', 'Roboto', monospace; color: #e0f2fe;
}
.ih-clock b { color: #fff; font-family: Roboto, Arial, sans-serif; }

/* Penalty clocks. */
.ih-pp { position: absolute; top: 6px; left: 8px; z-index: 2; display: flex; flex-direction: column; gap: 3px; }
.ih-pp-chip {
  display: flex; align-items: center; gap: 4px; padding: 1px 7px 1px 5px;
  background: rgba(0, 0, 0, 0.62); border-left: 2px solid; border-radius: 3px;
  font: 700 var(--font-size-2xl)/1.3 'Roboto Mono', 'Roboto', monospace; color: #cbd5e1;
}
.ih-pp-icon { width: 11px; height: 11px; color: #94a3b8; }

/* Penalty card. */
.ih-card-veil { position: absolute; inset: 0; z-index: 4; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.32); }
.ih-card {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  min-width: 170px; padding: 10px 26px 11px; border-radius: 6px;
  background: rgba(88, 86, 82, 0.94); border-bottom: 3px solid var(--side-color);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
}
.ih-card-jersey { width: 42px; height: 42px; margin-bottom: 2px; animation: ih-jersey 0.7s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
@keyframes ih-jersey { from { transform: scale(0.3) rotate(-20deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.ih-card-title { font: italic 900 var(--font-size-9xl)/1.05 'Roboto', sans-serif; color: #fff; animation: ih-rise 0.4s ease-out 0.2s both; }
.ih-card-min { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #7dd3fc; animation: ih-rise 0.4s ease-out 0.3s both; }
.ih-card-team { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #fff; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: ih-rise 0.4s ease-out 0.38s both; }
@keyframes ih-rise { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ih-card-enter-active { transition: opacity 0.25s ease; }
.ih-card-enter-active .ih-card { animation: ih-card-in 0.45s cubic-bezier(.2, 1.3, .4, 1) both; }
.ih-card-leave-active { transition: opacity 0.35s ease; }
.ih-card-enter, .ih-card-leave-to { opacity: 0; }
@keyframes ih-card-in { from { transform: scale(0.7); } to { transform: scale(1); } }

/* Goal celebration. */
.ih-goal-panel { position: absolute; inset: 0; z-index: 6; overflow: hidden; background: #0b0b0d; }
.ih-goal-act { position: absolute; inset: 0; }
.ih-goal-act--word {
  display: flex; align-items: center; justify-content: flex-end; padding-right: 11%;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.35) 100%), var(--goal-color);
}
/* Diagonal light lines sweeping across. */
.ih-goal-stripes {
  position: absolute; top: 0; bottom: 0; left: -40%; width: 180%;
  background: repeating-linear-gradient(125deg, transparent 0 58px, rgba(255, 255, 255, 0.32) 58px 59.5px, transparent 59.5px 104px);
  animation: ih-stripes 3s linear both;
}
@keyframes ih-stripes { from { transform: translateX(-12%); } to { transform: translateX(12%); } }
.ih-goal-words { position: relative; display: flex; flex-direction: column; align-items: flex-end; }
.ih-goal-word {
  font: italic 900 var(--font-size-19xl)/0.95 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.25);
  animation: ih-goal-word 0.55s cubic-bezier(.2, 1.4, .4, 1) 0.15s both;
}
@keyframes ih-goal-word { from { opacity: 0; transform: translateX(60px) scale(1.6); } to { opacity: 1; transform: translateX(0) scale(1); } }
.ih-goal-team {
  margin-top: 4px; max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font: italic 800 var(--font-size-8xl)/1.2 'Roboto', sans-serif; color: rgba(255, 255, 255, 0.7);
  animation: ih-goal-team 0.45s ease-out 0.5s both;
}
@keyframes ih-goal-team { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.ih-goal-act-enter-active { transition: opacity 0.3s ease; }
.ih-goal-act-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ih-goal-act-enter { opacity: 0; }
.ih-goal-act-leave-to { opacity: 0; transform: translateY(-18px); }

.ih-goal-act--split { display: flex; }
.ih-goal-half { flex: 1; display: flex; align-items: center; justify-content: center; animation: ih-half-in 0.45s cubic-bezier(.2, .9, .2, 1) both; }
.ih-goal-half--away { animation-delay: 0.08s; }
@keyframes ih-half-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
.ih-goal-digit { position: relative; width: 130px; height: 110px; overflow: hidden; }
.ih-goal-digit-value {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: italic 900 calc(var(--font-size-20xl) * 1.6)/1 'Roboto', sans-serif; font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.ih-goal-digit-value.is-intro { animation: ih-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) 0.4s both; }
@keyframes ih-roll-up { from { transform: translateY(110%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ih-roll-leave-active { transition: transform 0.22s cubic-bezier(.5, 0, .9, .4); }
.ih-roll-leave-to { transform: translateY(-110%); }
.ih-roll-enter-active { transition: transform 0.38s cubic-bezier(.15, .85, .35, 1); }
.ih-roll-enter { transform: translateY(110%); }
.ih-goal-panel-enter-active { transition: transform 0.5s cubic-bezier(.2, .9, .2, 1); }
.ih-goal-panel-leave-active { transition: transform 0.45s cubic-bezier(.6, 0, .8, .3); }
.ih-goal-panel-enter { transform: translateX(-105%); }
.ih-goal-panel-leave-to { transform: translateX(105%); }

/* Match over. */
.ih-final { position: absolute; inset: 0; z-index: 7; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.ih-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 28px 16px; max-width: 420px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: ih-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.ih-final-icon { width: 40px; height: 40px; }
.ih-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.ih-final-score { display: flex; gap: 8px; color: #cbd5e1; font-size: var(--font-size-5xl); font-weight: 700; white-space: nowrap; }
.ih-final-score .is-win { color: #fff; }
.ih-final-sep { opacity: 0.6; }
.ih-final-periods { display: flex; gap: 10px; margin-top: 2px; color: #94a3b8; font-size: var(--font-size-2xl); font-weight: 700; }
@keyframes ih-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.ih-final-enter-active { transition: opacity 0.3s; }
.ih-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
